import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-hot-toast';

// Components
import WritingPanel from './WritingPanel';
import LiveDraft from './LiveDraft';
import ContextualCoach from './ContextualCoach';

// Utils
import { analyzeForCoaching, getQualityIndicators } from './utils/coachingTriggers';

// Templates
import { personalGrowthTemplate } from './templates/personalGrowth';
import { overcomingChallengeTemplate } from './templates/overcomingChallenge';
import { passionProjectTemplate } from './templates/passionProject';

// Common App Templates
import { essentialIdentityTemplate } from './templates/essentialIdentityCommonApp';
import { learningFromFailureTemplate } from './templates/learningFromFailureCommonApp';
import { challengingAssumptionsTemplate } from './templates/challengingAssumptionsCommonApp';
import { unexpectedGratitudeTemplate } from './templates/unexpectedGratitudeCommonApp';
import { definingMomentTemplate } from './templates/definingMomentCommonApp';
import { intellectualPassionTemplate } from './templates/intellectualPassionCommonApp';

// Styles
import './ConversationalEssayBuilder.css';

const ConversationalEssayBuilder = ({
  selectedEssay,
  onSaveEssay,
  onExportEssay,
  onBack,
  initialTemplate = null,
  onControlsChange,
  templateRegistry = null,
  onWeave = null,
  renderTemplateSelection = null,
  wordGoal = 650
}) => {
  const navigate = useNavigate();
  
  // Template and sections
  const [selectedTemplate, setSelectedTemplate] = useState(initialTemplate);
  const [sections, setSections] = useState([]);
  const [currentStepIndex, setCurrentStepIndex] = useState(0);
  const [sectionResponses, setSectionResponses] = useState({});
  
  // Coaching state
  const [coachingMessage, setCoachingMessage] = useState(null);
  const [coachingType, setCoachingType] = useState('suggestion');
  const [showCoaching, setShowCoaching] = useState(false);
  const [lastAnalyzedText, setLastAnalyzedText] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  
  // Quality indicators
  const [qualityIndicators, setQualityIndicators] = useState([]);
  
  // Refs
  const coachingTimeoutRef = useRef(null);
  const saveTimeoutRef = useRef(null);
  const lastSavedContentRef = useRef(null);
  
  // Extract template from selectedEssay FIRST, before template initialization
  useEffect(() => {
    console.log('🔍 Template extraction useEffect triggered');
    
    if (selectedEssay && !selectedTemplate) {
      console.log('🔍 Attempting to extract template from essay:', {
        id: selectedEssay.id,
        hasBuilderData: !!selectedEssay.builder_data
      });
      
      try {
        // Check if we have builder_data first (new format)
        if (selectedEssay.builder_data) {
          const builderData = typeof selectedEssay.builder_data === 'string' 
            ? JSON.parse(selectedEssay.builder_data) 
            : selectedEssay.builder_data;
          
          console.log('🔍 ConversationalEssayBuilder: Loading essay with builder_data:', builderData);
          console.log('🔍 Template in builder_data:', builderData.template);
          
          if (builderData.template) {
            console.log('✅ Setting template from builder_data:', builderData.template);
            setSelectedTemplate(builderData.template);
            return; // Exit early if we found the template
          } else {
            console.log('⚠️ No template found in builder_data');
          }
        }
        
        // Fall back to parsing content if it's JSON
        if (selectedEssay.content) {
          try {
            const savedData = JSON.parse(selectedEssay.content);
            
            console.log('🔍 ConversationalEssayBuilder: Checking legacy content format:', savedData);
            console.log('🔍 Template in legacy content:', savedData.template);
            
            if (savedData.template) {
              console.log('✅ Setting template from legacy content:', savedData.template);
              setSelectedTemplate(savedData.template);
              return; // Exit early if we found the template
            } else {
              console.log('⚠️ No template found in legacy content');
            }
          } catch {
            console.log('📝 Content is plain text, not JSON');
          }
        }
      } catch (e) {
        console.error('❌ Error extracting template:', e);
      }
    } else if (selectedTemplate) {
      console.log('ℹ️ Template already set:', selectedTemplate);
    } else if (!selectedEssay) {
      console.log('ℹ️ No essay provided for template extraction');
    }
  }, [selectedEssay, selectedTemplate]);
  
  // Initialize template and load essay data
  useEffect(() => {
    console.log('🔄 Template initialization useEffect triggered with:', {
      selectedTemplate,
      selectedEssay: selectedEssay ? { id: selectedEssay.id, builder_data: !!selectedEssay.builder_data } : null
    });
    
    if (selectedTemplate) {
      let template;
      switch (selectedTemplate) {
        case 'personalGrowth':
          template = personalGrowthTemplate;
          break;
        case 'overcomingChallenge':
          template = overcomingChallengeTemplate;
          break;
        case 'passionProject':
          template = passionProjectTemplate;
          break;
        case 'essentialIdentity':
          template = essentialIdentityTemplate;
          break;
        case 'learningFromFailure':
          template = learningFromFailureTemplate;
          break;
        case 'challengingAssumptions':
          template = challengingAssumptionsTemplate;
          break;
        case 'unexpectedGratitude':
          template = unexpectedGratitudeTemplate;
          break;
        case 'definingMoment':
          template = definingMomentTemplate;
          break;
        case 'intellectualPassion':
          template = intellectualPassionTemplate;
          break;
        default:
          if (templateRegistry && templateRegistry[selectedTemplate]) {
            template = templateRegistry[selectedTemplate];
          } else {
            template = personalGrowthTemplate;
          }
      }
      setSections(template.sections);
      
      // Load saved essay data (responses, currentStep) - template extraction happens in separate useEffect
      if (selectedEssay) {
        try {
          console.log('📚 Loading essay data for template:', selectedTemplate);
          
          // Check if we have builder_data first (new format)
          if (selectedEssay.builder_data) {
            const builderData = typeof selectedEssay.builder_data === 'string' 
              ? JSON.parse(selectedEssay.builder_data) 
              : selectedEssay.builder_data;
            
            console.log('📚 Loading sections and progress from builder_data');
            
            if (builderData.sections) {
              setSectionResponses(builderData.sections);
            } else if (builderData.responses) {
              setSectionResponses(builderData.responses);
            }
            
            if (builderData.currentStep !== undefined) {
              setCurrentStepIndex(builderData.currentStep);
            }
          } 
          // Fall back to parsing content if it's JSON
          else if (selectedEssay.content) {
            try {
              const savedData = JSON.parse(selectedEssay.content);
              
              console.log('📚 Loading sections and progress from legacy content');
              
              if (savedData.responses) {
                setSectionResponses(savedData.responses);
              }
              if (savedData.currentStep !== undefined) {
                setCurrentStepIndex(savedData.currentStep);
              }
            } catch {
              // Content is plain text, not JSON - start fresh
              setSectionResponses({});
            }
          }
        } catch (e) {
          console.error('Error loading saved essay data:', e);
          setSectionResponses({});
        }
      }
    }
  }, [selectedTemplate, selectedEssay]);
  
  // Auto-save functionality
  useEffect(() => {
    if (saveTimeoutRef.current) {
      clearTimeout(saveTimeoutRef.current);
    }
    
    // Make sure sectionResponses is defined
    if (!sectionResponses || typeof sectionResponses !== 'object') {
      return;
    }
    
    const currentContent = JSON.stringify({
      template: selectedTemplate,
      responses: sectionResponses,
      currentStep: currentStepIndex
    });
    
    // Only save if content has changed
    if (currentContent !== lastSavedContentRef.current && Object.keys(sectionResponses).length > 0) {
      saveTimeoutRef.current = setTimeout(() => {
        handleAutoSave();
      }, 2000);
    }
    
    return () => {
      if (saveTimeoutRef.current) {
        clearTimeout(saveTimeoutRef.current);
      }
    };
  }, [sectionResponses, currentStepIndex, selectedTemplate]);
  
  const handleAutoSave = () => {
    if (!sectionResponses || typeof sectionResponses !== 'object') {
      return;
    }
    
    // Calculate word count
    const compiledText = Object.values(sectionResponses).filter(text => text).join('\n\n');
    const wordCount = compiledText.trim() ? compiledText.trim().split(/\s+/).length : 0;
    
    // Create the save data in the format the parent expects
    const saveData = {
      title: selectedEssay?.title || 'Essay Draft',
      sections: sectionResponses,
      template: selectedTemplate,
      currentStep: currentStepIndex,
      wordCount: wordCount,
      content: compiledText // Include plain text content
    };
    
    lastSavedContentRef.current = JSON.stringify(saveData);
    
    if (onSaveEssay) {
      onSaveEssay(saveData);
    }
  };
  
  // Handle response changes with coaching analysis
  const handleResponseChange = useCallback((value) => {
    const currentSection = sections[currentStepIndex];
    if (!currentSection) return;
    
    setSectionResponses(prev => ({
      ...(prev || {}),
      [currentSection.id]: value
    }));
    
    // Clear existing coaching timeout
    if (coachingTimeoutRef.current) {
      clearTimeout(coachingTimeoutRef.current);
    }
    
    // Analyze for coaching triggers
    const wordCount = value && value.trim ? value.trim().split(/\s+/).length : 0;
    
    if (wordCount > 0 && value !== lastAnalyzedText) {
      const trigger = analyzeForCoaching(value, wordCount, currentSection.id);
      
      if (trigger) {
        const delay = trigger.immediate ? 0 : (trigger.delay || 2000);
        coachingTimeoutRef.current = setTimeout(() => {
          setCoachingMessage(trigger.message);
          setCoachingType(trigger.type);
          setShowCoaching(true);
          setLastAnalyzedText(value);
        }, delay);
      }
    }
    
    // Update quality indicators
    const updatedResponses = {
      ...(sectionResponses || {}),
      [currentSection.id]: value
    };
    const compiledEssay = Object.values(updatedResponses).filter(text => text).join(' ');
    const totalWords = compiledEssay.trim ? compiledEssay.trim().split(/\s+/).length : 0;
    setQualityIndicators(getQualityIndicators(compiledEssay, totalWords));
  }, [sections, currentStepIndex, sectionResponses, lastAnalyzedText]);
  
  // Manual save handler
  const handleManualSave = async () => {
    setIsSaving(true);
    try {
      await handleAutoSave();
      toast.success('Essay saved successfully!');
    } catch (error) {
      toast.error('Failed to save essay');
      console.error('Save error:', error);
    } finally {
      setIsSaving(false);
    }
  };
  
  // Weave handler - transition to refinement mode
  const handleWeave = () => {
    // Save before transitioning
    handleAutoSave();

    // Create essay data for saving
    const essayData = {
      template: selectedTemplate,
      sections: sectionResponses,
      wordCount: Object.values(sectionResponses).filter(text => text).join(' ').trim().split(/\s+/).length,
      lastUpdated: new Date().toISOString()
    };

    // Compile the essay content from all sections
    const compiledEssay = sections
      .map(section => sectionResponses[section.id] || '')
      .filter(text => text.length > 0)
      .map(text => text.trim())
      .join('\n\n');

    // Allow parent to override weave behavior
    if (onWeave) {
      onWeave({
        ...selectedEssay,
        title: selectedEssay?.title || 'Essay Draft',
        content: compiledEssay,
        builder_data: essayData
      });
      return;
    }

    toast.success('Time to refine your essay! 🎨');

    // Navigate to refinement interface with essay data
    navigate('/college/essays/refinement', {
      state: {
        essay: {
          ...selectedEssay,
          title: selectedEssay?.title || 'Essay Draft',
          content: compiledEssay,
          builder_data: essayData
        }
      }
    });
  };
  
  // Navigation handlers
  const handleContinue = () => {
    if (currentStepIndex < sections.length - 1) {
      setCurrentStepIndex(currentStepIndex + 1);
      setShowCoaching(false);
      toast.success(`Moving to Step ${currentStepIndex + 2}`);
    }
  };
  
  const handleBack = () => {
    if (currentStepIndex > 0) {
      setCurrentStepIndex(currentStepIndex - 1);
    } else if (onBack) {
      onBack();
    }
  };
  
  // Coaching handlers
  const handleApplyCoaching = () => {
    // This could be expanded to actually apply specific suggestions
    toast.success('Suggestion noted! Keep writing.');
    setShowCoaching(false);
  };
  
  const handleDismissCoaching = () => {
    setShowCoaching(false);
  };
  
  const handleIgnoreCoaching = () => {
    setShowCoaching(false);
    // Could track ignored suggestions for analytics
  };
  
  // Template selection screen
  if (!selectedTemplate) {
    if (renderTemplateSelection) {
      return renderTemplateSelection(setSelectedTemplate);
    }
    return (
      <div className="template-selection">
        <h2>Choose Your Essay Template</h2>
        
        {/* General Templates Section */}
        <div className="template-section">
          <h3 className="template-section-title">General Templates</h3>
          <div className="template-grid">
            <button 
              className="template-card"
              onClick={() => setSelectedTemplate('personalGrowth')}
            >
              <h4>Personal Growth Journey</h4>
              <p>Perfect for overcoming limitations and building confidence</p>
            </button>
            
            <button 
              className="template-card"
              onClick={() => setSelectedTemplate('overcomingChallenge')}
            >
              <h4>Overcoming a Challenge</h4>
              <p>Show resilience through difficult circumstances</p>
            </button>
            
            <button 
              className="template-card"
              onClick={() => setSelectedTemplate('passionProject')}
            >
              <h4>Passion Project</h4>
              <p>Highlight your interests and creative pursuits</p>
            </button>
          </div>
        </div>

        {/* Common App Templates Section */}
        <div className="template-section">
          <h3 className="template-section-title">
            Common App Prompts
            <span className="common-app-badge">Common App</span>
          </h3>
          <div className="template-grid">
            <button 
              className="template-card common-app-card"
              onClick={() => setSelectedTemplate('essentialIdentity')}
            >
              <h4>Essential Identity</h4>
              <p>For backgrounds, identities, interests, or talents so meaningful your application would be incomplete without them</p>
            </button>
            
            <button 
              className="template-card common-app-card"
              onClick={() => setSelectedTemplate('learningFromFailure')}
            >
              <h4>Learning from Failure</h4>
              <p>For recounting challenges, setbacks, or failures that taught fundamental lessons leading to later success</p>
            </button>
            
            <button 
              className="template-card common-app-card"
              onClick={() => setSelectedTemplate('challengingAssumptions')}
            >
              <h4>Challenging Assumptions</h4>
              <p>For times when you questioned or challenged a belief or idea, showing intellectual courage and independent thinking</p>
            </button>
            
            <button 
              className="template-card common-app-card"
              onClick={() => setSelectedTemplate('unexpectedGratitude')}
            >
              <h4>Unexpected Gratitude</h4>
              <p>For reflecting on something someone did that made you happy or thankful in a surprising way</p>
            </button>
            
            <button 
              className="template-card common-app-card"
              onClick={() => setSelectedTemplate('definingMoment')}
            >
              <h4>Defining Moment</h4>
              <p>For accomplishments, events, or realizations that sparked personal growth and new understanding of yourself or others</p>
            </button>
            
            <button 
              className="template-card common-app-card"
              onClick={() => setSelectedTemplate('intellectualPassion')}
            >
              <h4>Intellectual Passion</h4>
              <p>For topics, ideas, or concepts so engaging that they make you lose track of time - showcasing deep intellectual curiosity</p>
            </button>
          </div>
        </div>
      </div>
    );
  }
  
  // Ensure we have sections before rendering
  if (!sections || sections.length === 0) {
    return (
      <div className="essay-builder-container">
        <div className="essay-builder-layout">
          <div className="template-selection">
            <p>Loading essay template...</p>
          </div>
        </div>
      </div>
    );
  }
  
  const currentSection = sections[currentStepIndex];
  const currentResponse = sectionResponses && currentSection ? (sectionResponses[currentSection.id] || '') : '';
  
  // Calculate total word count and check if all sections have content
  const totalWordCount = Object.values(sectionResponses || {})
    .filter(text => text)
    .join(' ')
    .trim()
    .split(/\s+/).length;
  
  const allSectionsHaveContent = sections.every(section => 
    sectionResponses[section.id] && sectionResponses[section.id].trim().length > 0
  );
  
  return (
    <div className="essay-builder-container">
      <div className="essay-builder-layout">
        {/* Left Column - Writing Panel */}
        <WritingPanel
          template={selectedTemplate}
          currentStep={currentSection}
          currentStepIndex={currentStepIndex}
          totalSteps={sections.length}
          response={currentResponse}
          onResponseChange={handleResponseChange}
          onContinue={handleContinue}
          onBack={handleBack}
          onSave={handleManualSave}
          onWeave={handleWeave}
          wordGoal={currentSection?.minWords || 80}
          isLastStep={currentStepIndex === sections.length - 1}
          isSaving={isSaving}
          totalWordCount={totalWordCount}
          allSectionsHaveContent={allSectionsHaveContent}
        />
        
        {/* Right Column - Live Draft */}
        <LiveDraft
          sections={sections}
          sectionResponses={sectionResponses}
          currentStepIndex={currentStepIndex}
          wordGoal={wordGoal}
          qualityIndicators={qualityIndicators}
        />
      </div>
      
      {/* Contextual Coaching */}
      <ContextualCoach
        isVisible={showCoaching}
        message={coachingMessage}
        type={coachingType}
        onApply={handleApplyCoaching}
        onDismiss={handleDismissCoaching}
        onIgnore={handleIgnoreCoaching}
        autoDismissTime={8000}
      />
    </div>
  );
};

export default ConversationalEssayBuilder;